// @flow

import React, { useState, useContext, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Heading,
  Paragraph,
  Link,
  Icon,
  Input,
  Button
} from 'operation-diameter';

import { useForm } from 'hooks/useForm';
import { useCopy } from 'hooks/useCopy';
import { scrollToElement } from 'shared/components/scrollToElement';
import { FormContext } from 'shared/formContext/FormContext';
import canApply from 'shared/requests/canApply';
import MFAMigrated from 'shared/requests/MFAMigrated';
import { getCompanySegment } from 'shared/requests/getCompanySegment';
import currentBorrower from 'shared/requests/currentBorrower';
import sendMagicLink from 'shared/requests/sendMagicLink';
import { TrackingContext } from 'shared/tracking/TrackingContext';
import { email as emailValidation } from 'shared/validations';
import LoginNotice from './LoginNotice';
import MagicLinkSuccess from './MagicLinkSuccess';

import styles from './LoginNotice.module.css';

const MagicLinkStatus = {
  PROCESSING: 'processing',
  SUCCESS: 'success'
};

const ContactDetails = () => {
  const eligibility = useContext(FormContext);
  const { push } = useHistory();
  const { setCustomDimensionValue } = useContext(TrackingContext);
  const [shouldShowNotice, setShowNotice] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [inputLock, setInputLock] = useState(true);
  const [isMFAMigrated, setIsMFAMigrated] = useState(false);
  const [magicLinkStatus, setMagicLinkStatus] = useState(null);
  const magicLinkProcessing = magicLinkStatus === MagicLinkStatus.PROCESSING;
  const loginNoticeRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  const fields = {
    email: {
      initialState: eligibility.fields.email,
      validations: [emailValidation]
    }
  };

  const { email, isFormValid, runValidations } = useForm({
    fields
  });

  const { getCopy } = useCopy('borrower');

  const scrollLoginNoticeIntoView = () => {
    scrollToElement(loginNoticeRef, { block: 'center' });
  };

  useEffect(() => {
    const fetchCurrentLoggedInBorrower = async () => {
      const borrower = await currentBorrower();

      borrower ? email.set(borrower.email) : setInputLock(false);
      setIsLoading(false);
    };

    fetchCurrentLoggedInBorrower();
  }, []);

  const onSubmit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    runValidations();

    if (isFormValid) {
      eligibility.setFields({ email: email.value });
      setIsLoading(true);

      const [isNewOrLoggedInAccount, companySegment, isUserMFAMigrated] =
        await Promise.all([
          canApply(email.value).catch(() => {}),
          getCompanySegment(eligibility.fields.companyCodasId).catch(() => {}),
          MFAMigrated(email.value).catch(() => {})
        ]);

      setIsLoading(false);
      setIsMFAMigrated(isUserMFAMigrated);

      if (companySegment) {
        setCustomDimensionValue('companySegment', companySegment);
      }

      if (!isNewOrLoggedInAccount) {
        if (shouldShowNotice) {
          scrollLoginNoticeIntoView();
        } else {
          setShowNotice(true);
        }
        return;
      }

      eligibility.submit();
      setIsSubmitted(true);
    }
  };

  const onSendMagicLinkClick = async () => {
    setIsLoading(true);
    setMagicLinkStatus(MagicLinkStatus.PROCESSING);

    try {
      await sendMagicLink(email.value);
      setIsLoading(false);
    } catch (err) {
      push('/error');
      return;
    }

    setMagicLinkStatus(MagicLinkStatus.SUCCESS);
  };

  useEffect(() => {
    const isNoticeVisible = !!loginNoticeRef.current;
    if (isNoticeVisible) {
      scrollLoginNoticeIntoView();
    }
  }, [shouldShowNotice]);

  return magicLinkStatus === MagicLinkStatus.SUCCESS ? (
    <MagicLinkSuccess
      email={email.value}
      onCloseClick={() => {
        setMagicLinkStatus(null);
      }}
    />
  ) : (
    <div className={styles.contactDetailsWrapper} data-test-loading={isLoading}>
      <div className={styles.contactDetailsHeading}>
        <Heading headingSize='h6'>What’s your email address?</Heading>

        <Paragraph>
          We’ll use this if we need to contact you about your application.
        </Paragraph>
      </div>
      <form className={styles.emailInputForm} onSubmit={onSubmit}>
        <div className={styles.inputWrapper}>
          <Input
            label='Email address'
            type='email'
            name='email'
            value={email.value}
            onChange={({ currentTarget }) => {
              email.set(currentTarget.value);
              shouldShowNotice && setShowNotice(false);
            }}
            {...(email.error && { error: email.error })}
            isRequired
            isDisabled={inputLock}
          />

          {shouldShowNotice && (
            <div ref={loginNoticeRef}>
              <LoginNotice
                magicLinkProcessing={magicLinkProcessing}
                onSendMagicLinkClick={onSendMagicLinkClick}
                showMFALoginOption={isMFAMigrated}
              />
            </div>
          )}
        </div>
        <div className={styles.contactDetailsTerms}>
          <Paragraph paragraphSize='p2'>
            By selecting “Check eligibility” you agree to our{' '}
            <Link size='small' href='/uk/legal/borrower-agreement/'>
              Funding Circle Borrower Agreement
            </Link>{' '}
            and{' '}
            <Link size='small' href='/uk/legal/privacy-policy/'>
              {' '}
              Privacy Policy
            </Link>
            .
          </Paragraph>
          <div className={styles.highlightText}>
            <Icon icon='success' isDecorative />
            <Paragraph paragraphSize='p2'>
              {getCopy('eligibility.does-not-affect-credit-score')}
            </Paragraph>
          </div>
        </div>

        <Button
          size='large'
          data-process-text='Processing'
          isDisabled={isSubmitted || isLoading}
          type='submit'
          data-testid='eligibility-button'
          data-cy='eligibility-button'
          additionalClassnames={styles.buttonContent}>
          Check eligibility
        </Button>
      </form>
    </div>
  );
};

export default ContactDetails;
